import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Camera,
  Lock,
  Save,
  X,
  User,
  Mail,
  Phone,
  Briefcase,
  Users,
  MapPin,
} from "lucide-react";
import PageWrapper from "../../components/layout/PageWrapper";
import Avatar from "../../components/ui/Avatar";
import Button from "../../components/forms/Button";
import ChangePasswordModal from "../../components/modals/ChangePasswordModal";
import Toast from "../../components/ui/Toast";
import useAuth from "../../hooks/useAuth";
import { resizeImage } from "../../utils/imageResize";
import ImageCropModal from "../../components/modals/ImageCropModal";
import {
  uploadImage,
  isSupabaseStorageAvailable,
  IMAGES_BUCKET,
} from "../../api/supabaseStorage";

const Profile = () => {
  const { employee, fetchEmployee } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [nickname, setNickname] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    message: "",
    type: "success",
  });
  const fileInputRef = useRef(null);
  const [cropSource, setCropSource] = useState(null);
  const initialLoadRef = useRef(true);

  const showToast = (message, type = "success") => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast({ isVisible: false, message: "", type: "success" });
  };

  // Initialize state when employee data loads
  useEffect(() => {
    if (employee && initialLoadRef.current) {
      setProfileImage(employee.photo || null);
      setNickname(employee.nickname || "");
      initialLoadRef.current = false;
    }
  }, [employee]);

  // Check if there are unsaved changes (only after initial load)
  useEffect(() => {
    if (!initialLoadRef.current && employee) {
      const imageChanged = profileImage !== (employee.photo || null);
      const nicknameChanged = nickname !== (employee.nickname || "");
      setHasChanges(imageChanged || nicknameChanged);
    }
  }, [profileImage, nickname, employee]);

  // Format employee data for display
  const formatTitle = (title) => {
    const titleMap = {
      team_leader: "Team Leader",
      senior: "Senior",
      mid_level: "Mid Level",
      junior: "Junior",
      intern: "Intern",
    };
    return titleMap[title] || title;
  };

  const formatTeam = (team) => {
    const teamMap = {
      high_potit: "High Potit",
      backend: "Backend",
      frontend: "Frontend",
      tester: "Tester",
      ra: "Requirement Analyst",
      mobile_app: "Mobile App",
      uiux: "UI/UX",
    };
    return teamMap[team] || team;
  };

  const employeeData = {
    fullName: employee?.fullNameEn || "User Name",
    fullNameAr: employee?.fullNameAr || "",
    nickname: employee?.nickname || "user",
    email: employee?.email || "",
    phone: employee?.phone || "",
    role: employee?.role || "employee",
    title: formatTitle(employee?.title) || "",
    team: formatTeam(employee?.team) || "",
    teams: employee?.teams?.length
      ? employee.teams.map((t) => ({
          team: formatTeam(typeof t === "string" ? t : t.team),
          title: formatTitle(typeof t === "string" ? "" : t.title),
        }))
      : [],
    branch: employee?.branch || "",
    employeeCode: employee?.employeeCode || "",
    avatar: employee?.photo || null,
  };

  const MAX_IMAGE_SIZE_BYTES = 1024 * 1024; // 1 MB (applied after cropping)

  // Picking a file opens the crop modal; the upload happens on confirm.
  const handleImageChange = (e) => {
    const picked = e.target.files[0];
    e.target.value = "";
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      showToast("Please choose an image file.", "error");
      return;
    }
    setCropSource(picked);
  };

  const handleCropConfirm = async (croppedFile) => {
    let file;
    try {
      file = await resizeImage(croppedFile, { maxBytes: MAX_IMAGE_SIZE_BYTES });
    } catch (err) {
      showToast(err?.message || "Could not process this image.", "error");
      return;
    }
    await uploadAvatar(file);
  };

  const uploadAvatar = async (file) => {
    // Profile images are stored only in Supabase (not as base64 on backend or frontend)
    if (!isSupabaseStorageAvailable()) {
      showToast(
        "Profile photos use Supabase storage. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.",
        "error",
      );
      return;
    }

    if (!employee?._id) {
      showToast("Unable to upload: user not loaded.", "error");
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `avatars/${employee._id}/${Date.now()}.${ext}`;
    const { publicUrl, error } = await uploadImage(IMAGES_BUCKET, path, file);
    if (error) {
      showToast(error?.message || "Upload failed. Try again.", "error");
      return;
    }
    if (publicUrl) {
      setProfileImage(publicUrl);
    }
    setCropSource(null);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSaveChanges = async () => {
    if (!employee) return;

    setIsSaving(true);
    try {
      // Prepare update data (only send changed fields)
      const updateData = {};

      if (nickname !== employee.nickname) {
        updateData.nickname = nickname;
      }

      if (profileImage !== employee.photo) {
        updateData.photo = profileImage;
      }

      if (Object.keys(updateData).length === 0) {
        setHasChanges(false);
        return;
      }

      // Update own profile (no admin required)
      const { axiosPrivate } = await import("../../api/axios");
      await axiosPrivate.put("/api/employees/me", updateData);

      // Refresh employee data
      await fetchEmployee();

      setHasChanges(false);
      initialLoadRef.current = true; // Reset to prevent immediate change detection

      showToast("Profile updated successfully!", "success");
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update profile. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    setProfileImage(employee?.photo || null);
    setNickname(employee?.nickname || "");
    setHasChanges(false);
  };

  return (
    <PageWrapper
      title="Profile"
      subtitle="Manage your account settings and personal information"
    >
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="xl:col-span-4 card"
        >
          <div className="flex flex-col items-center text-center p-6">
            {/* Avatar with hover-to-edit overlay */}
            <div className="relative group mb-4 w-24 h-24">
              <div className="relative w-full h-full rounded-full overflow-hidden ring-2 ring-transparent group-hover:ring-primary-accent/50 transition-all duration-300">
                <Avatar
                  src={profileImage}
                  alt={employeeData.fullName}
                  size="2xl"
                  preview={false}
                  className="transition-transform duration-300 group-hover:scale-105"
                />
                {hasChanges && profileImage !== (employee?.photo || null) && (
                  <div
                    className="absolute top-0 right-0 w-4 h-4 bg-primary-accent rounded-full border-2 border-white dark:border-primary-card z-10"
                    title="Unsaved change"
                  />
                )}
                {/* Hover overlay: edit indicator */}
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/60"
                  title="Change photo"
                  aria-label="Change profile photo"
                >
                  <Camera
                    className="w-8 h-8 text-white drop-shadow-md"
                    strokeWidth={2}
                  />
                  <span className="text-[10px] font-medium text-white drop-shadow-md uppercase tracking-wider">
                    Edit
                  </span>
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>

            {/* Name and Nickname */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-light-100 mb-2">
              {employeeData.fullName}
            </h2>

            {/* Editable Nickname */}
            <div className="w-full max-w-xs mb-4">
              <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 dark:bg-dark-300 rounded-lg border-2 border-transparent focus-within:border-primary-accent transition-colors">
                <span className="text-gray-500 dark:text-light-500">@</span>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="bg-transparent border-none outline-none text-sm text-center text-gray-700 dark:text-light-300 w-full font-medium"
                  placeholder="nickname"
                />
              </div>
            </div>

            {/* Title & Team Badge(s) */}
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {employeeData.teams.length > 0 ? (
                employeeData.teams.map((t, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-primary-accent to-orange-600 text-white rounded-full font-medium text-xs shadow-md"
                  >
                    <Briefcase className="w-3.5 h-3.5" />
                    <span>{[t.title, t.team].filter(Boolean).join(" • ")}</span>
                  </div>
                ))
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-accent to-orange-600 text-white rounded-full font-medium text-sm shadow-lg">
                  <Briefcase className="w-4 h-4" />
                  <span>
                    {[employeeData.title, employeeData.team]
                      .filter(Boolean)
                      .join(" • ") || "User"}
                  </span>
                </div>
              )}
            </div>

            {/* Employee Code */}
            <div className="w-full px-4 py-3 bg-gray-50 dark:bg-dark-300 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-light-500 mb-1">
                Employee Code
              </p>
              <p className="text-sm font-mono font-semibold text-gray-900 dark:text-light-100">
                {employeeData.employeeCode}
              </p>
            </div>

            {/* Save/Cancel Buttons */}
            {hasChanges && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full mt-6 flex gap-3"
              >
                <Button
                  variant="ghost"
                  onClick={handleCancelChanges}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-primary-accent to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save
                    </>
                  )}
                </Button>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Profile Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="xl:col-span-8 space-y-6"
        >
          {/* Personal Information Card */}
          <div className="card">
            <div className="border-b border-gray-200 dark:border-dark-100 pb-4 mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-light-100">
                Personal Information
              </h3>
              <p className="text-sm text-gray-500 dark:text-light-500 mt-1">
                View your personal and contact information
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-light-300">
                  <User className="w-4 h-4 text-primary-accent" />
                  Full Name
                </label>
                <div className="px-4 py-3 bg-gray-50 dark:bg-dark-300 rounded-lg text-gray-900 dark:text-light-100">
                  {employeeData.fullName}
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-light-300">
                  <Mail className="w-4 h-4 text-primary-accent" />
                  Email
                </label>
                <div className="px-4 py-3 bg-gray-50 dark:bg-dark-300 rounded-lg text-gray-900 dark:text-light-100">
                  {employeeData.email}
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-light-300">
                  <Phone className="w-4 h-4 text-primary-accent" />
                  Phone
                </label>
                <div className="px-4 py-3 bg-gray-50 dark:bg-dark-300 rounded-lg text-gray-900 dark:text-light-100">
                  {employeeData.phone}
                </div>
              </div>

              {/* Branch */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-light-300">
                  <MapPin className="w-4 h-4 text-primary-accent" />
                  Branch
                </label>
                <div className="px-4 py-3 bg-gray-50 dark:bg-dark-300 rounded-lg text-gray-900 dark:text-light-100">
                  {employeeData.branch}
                </div>
              </div>

              {/* Teams & Titles */}
              <div className="space-y-2 md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-light-300">
                  <Users className="w-4 h-4 text-primary-accent" />
                  {employeeData.teams.length > 1
                    ? "Teams & Titles"
                    : "Team & Title"}
                </label>
                {employeeData.teams.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {employeeData.teams.map((t, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-dark-300 rounded-lg border border-gray-200 dark:border-dark-200"
                      >
                        <span className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {t.team}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-light-500">
                          —
                        </span>
                        <span className="text-sm text-primary-accent font-medium">
                          {t.title}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 bg-gray-50 dark:bg-dark-300 rounded-lg text-gray-900 dark:text-light-100">
                    {[employeeData.team, employeeData.title]
                      .filter(Boolean)
                      .join(" — ") || "—"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Security Card */}
          <div className="card">
            <div className="border-b border-gray-200 dark:border-dark-100 pb-4 mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-light-100 flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary-accent" />
                Security Settings
              </h3>
              <p className="text-sm text-gray-500 dark:text-light-500 mt-1">
                Manage your password and account security
              </p>
            </div>

            <div className="bg-gradient-to-r from-primary-accent/10 to-orange-600/10 dark:from-primary-accent/20 dark:to-orange-600/20 rounded-lg p-6 border border-primary-accent/20">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-accent to-orange-600 rounded-lg flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-light-100 mb-2">
                    Password Protection
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-light-400 mb-4">
                    Keep your account secure by using a strong password. We
                    recommend changing your password regularly.
                  </p>
                  <Button
                    onClick={() => setShowPasswordModal(true)}
                    className="bg-gradient-to-r from-primary-accent to-orange-600 hover:from-primary-accent/90 hover:to-orange-600/90 flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Change Password
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <ImageCropModal
        file={cropSource}
        onCancel={() => setCropSource(null)}
        onConfirm={handleCropConfirm}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </PageWrapper>
  );
};

export default Profile;
